import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gift, ShoppingBag, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CartLineRow } from "@/components/cart/CartLineRow";
import { EmptyState, Spinner } from "@/components/states";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { formatDate, formatPrice } from "@/lib/format";
import { featureEnabled, useStorefront } from "@/lib/storefront";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { CustomerErrorViewer } from "@/components/customer-error-viewer";
import { LoyaltyReward } from "@/components/cart/LoyaltyReward";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MenuItemImage } from "@/components/menu/MenuItemImage";
import { ProductDetailConfigurator } from "@/components/menu/ProductCard";
import { checkoutSuggestionsQuery } from "@/lib/queries";
import { loc } from "@/lib/format";
import type { MenuItem } from "@/lib/types";
import {
  availableCheckoutSuggestions,
  menuItemDisplayPrice,
} from "@/lib/menu-model";

export function CartTotals({ comfortable = false }: { comfortable?: boolean }) {
  const { t, lang } = useI18n();
  const {
    cart,
    subtotal,
    deliveryFee,
    serviceFee,
    discount,
    tax,
    total,
    quote,
    quotePending,
    quoteError,
    refreshQuote,
  } = useCart();
  const promotion = quote?.promotions[0];
  const deliveryFeeBeforeDiscount =
    quote?.pricing.deliveryFeeBeforeDiscountMinor ?? deliveryFee;

  return (
    <div
      className={cn(
        "grid border-t border-border text-sm",
        comfortable ? "gap-3 pt-4" : "gap-2 pt-3",
      )}
    >
      <Row label={t("cart.subtotal")} value={formatPrice(subtotal, lang)} />
      {cart.orderType?.type === "delivery" ? (
        <Row
          label={t("cart.deliveryFee")}
          value={
            quote
              ? formatPrice(deliveryFeeBeforeDiscount, lang)
              : t("cart.feePending")
          }
        />
      ) : null}
      {discount ? (
        <Row
          label={
            promotion?.name[lang] ??
            promotion?.name["en"] ??
            (quote?.loyalty?.applied
              ? t("loyalty.cartDiscount")
              : t("cart.discount"))
          }
          value={`- ${formatPrice(discount, lang)}`}
          tone="discount"
        />
      ) : null}
      {serviceFee ? (
        <Row
          label={t("cart.serviceFee")}
          value={formatPrice(serviceFee, lang)}
        />
      ) : null}
      {tax && !quote?.pricing.pricesIncludeTax ? (
        <Row label={t("cart.tax")} value={formatPrice(tax, lang)} />
      ) : null}
      {cart.orderType?.type === "pickup" && cart.orderType.pickupTimeISO ? (
        <Row
          label={t("orderType.pickupTime")}
          value={formatDate(cart.orderType.pickupTimeISO, lang, true)}
        />
      ) : null}
      <div
        className={cn(
          "mt-1 flex items-center justify-between border-t border-border/70 pt-3 font-extrabold",
          comfortable ? "text-lg" : "text-base",
        )}
      >
        <span>{t(quote ? "cart.total" : "cart.estimatedTotal")}</span>
        <span className="text-foreground">{formatPrice(total, lang)}</span>
      </div>
      {quoteError && !quotePending ? (
        <CustomerErrorViewer
          details={quoteError}
          compact
          actionLabel={t("cart.updateTotal")}
          actionDisabled={quotePending}
          onAction={() => void refreshQuote().catch(() => undefined)}
        />
      ) : null}
    </div>
  );
}

function Row({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "discount";
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4",
        tone === "discount" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      <span>{label}</span>
      <span
        className={cn(
          "text-end font-bold tabular-nums",
          tone === "discount" ? "text-destructive" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function CartPanel({
  onCheckout,
  onClear,
  showHeader = true,
  variant = "compact",
}: {
  onCheckout?: (() => boolean | void) | undefined;
  onClear?: (() => void) | undefined;
  showHeader?: boolean | undefined;
  variant?: "compact" | "page" | undefined;
}) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const bootstrap = useStorefront();
  const {
    cart,
    addLine,
    setQuantity,
    removeLine,
    lineCount,
    hydrated,
    quote,
    quotePending,
    setPromotionCode,
    clearPromotionAttribution,
  } = useCart();
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [addingSuggestion, setAddingSuggestion] = useState<string | null>(null);
  const { data: suggestedItems } = useQuery(
    checkoutSuggestionsQuery(
      cart.orderType?.branchId ?? "",
      cart.orderType?.type ?? "pickup",
    ),
  );
  const suggestions = availableCheckoutSuggestions(
    suggestedItems ?? [],
    cart.lines,
  );

  useEffect(() => {
    if (suggestionsOpen && suggestedItems && suggestions.length === 0) {
      setSuggestionsOpen(false);
      setConfiguring(null);
    }
  }, [suggestionsOpen, suggestedItems, suggestions.length]);

  function continueCheckout() {
    setSuggestionsOpen(false);
    if (onCheckout?.() === false) return;
    void navigate({ to: "/checkout" });
  }

  async function quickAdd(item: MenuItem) {
    if (addingSuggestion) return;
    setAddingSuggestion(item.id);
    try {
      await addLine({
        itemId: item.id,
        slug: item.slug,
        name: item.name,
        image: item.image,
        unitPrice: item.price,
        additions: [],
        quantity: 1,
      });
      toast.success(t("menu.added"));
    } catch {
      // The cart provider shows the validated quote error.
    } finally {
      setAddingSuggestion(null);
    }
  }
  const [promotionCode, setPromotionCodeInput] = useState(
    cart.promotionCode ?? "",
  );
  const codeApplied = quote?.promotions.some(
    (promotion) =>
      promotion.code?.toLowerCase() === cart.promotionCode?.toLowerCase(),
  );
  useEffect(
    () => setPromotionCodeInput(cart.promotionCode ?? ""),
    [cart.promotionCode],
  );

  const linkedAttribution =
    cart.promotionAttribution?.source === "link"
      ? (cart.linkedPromotionAttribution ?? cart.promotionAttribution)
      : undefined;
  const linkedOffer = linkedAttribution ? (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-primary/25 bg-primary/5 p-3">
      <div className="flex min-w-0 gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <Gift className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold">{t("cart.linkedOffer")}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {linkedAttribution.promotionName[lang] ||
              linkedAttribution.promotionName.en ||
              linkedAttribution.promotionName.ar}
          </p>
          <p className="mt-1 text-xs text-success">
            {t("cart.linkedOfferAutomatic")}
          </p>
        </div>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-label={t("cart.removeLinkedOffer")}
        onClick={clearPromotionAttribution}
      >
        <X className="size-4" />
      </Button>
    </div>
  ) : null;

  const promotionField = featureEnabled(bootstrap, "promotions") ? (
    <div className="grid gap-2">
      <label
        htmlFor={`cart-promotion-${variant}`}
        className="text-sm font-bold"
      >
        {t("cart.promotionLabel")}
      </label>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPromotionCode(promotionCode);
        }}
      >
        <Input
          id={`cart-promotion-${variant}`}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          value={promotionCode}
          onChange={(event) => setPromotionCodeInput(event.target.value)}
          placeholder={t("cart.promotionPlaceholder")}
          className="h-11 rounded-xl"
        />
        <Button
          type="submit"
          variant="outline"
          disabled={!promotionCode.trim() || quotePending}
        >
          {t("cart.applyPromotion")}
        </Button>
      </form>
      {cart.promotionCode ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          {quotePending ? (
            <span role="status">{t("cart.promotionChecking")}</span>
          ) : codeApplied ? (
            <span className="text-success" role="status">
              {t("cart.promotionApplied", { code: cart.promotionCode })}
            </span>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPromotionCode("")}
          >
            {t("cart.removePromotion")}
          </Button>
        </div>
      ) : null}
    </div>
  ) : null;

  const checkoutButton =
    bootstrap.mode === "preview" || !quote || quotePending ? (
      <Button size="lg" className="w-full" disabled>
        {bootstrap.mode === "preview"
          ? t("cart.previewDisabled")
          : quotePending
            ? t("cart.pleaseWait")
            : t("cart.checkoutUnavailable")}
      </Button>
    ) : (
      <Button asChild size="lg" className="w-full">
        <Link
          to="/checkout"
          onClick={(event) => {
            if (suggestions.length) {
              event.preventDefault();
              setSuggestionsOpen(true);
              return;
            }
            if (onCheckout?.() === false) event.preventDefault();
          }}
        >
          {t("cart.checkout")}
        </Link>
      </Button>
    );

  const suggestionsDialog = (
    <Dialog
      open={suggestionsOpen}
      onOpenChange={(open) => {
        setSuggestionsOpen(open);
        if (!open) setConfiguring(null);
      }}
    >
      <DialogContent
        className="max-h-[85vh] overflow-y-auto sm:max-w-xl"
        onCloseClick={continueCheckout}
      >
        <DialogHeader>
          <DialogTitle>{t("cart.suggestionsTitle")}</DialogTitle>
          <DialogDescription>
            {t(
              suggestions.length
                ? "cart.suggestionsDescription"
                : "cart.suggestionsAdded",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {suggestions.map((item) => {
            const directAdd =
              item.type === "simple" &&
              item.additionGroups.every((group) => group.minimum === 0);
            return (
              <article
                key={item.id}
                className="rounded-2xl border border-border p-3"
              >
                <div className="flex items-center gap-3">
                  <MenuItemImage
                    source={item.responsiveImage ?? item.image}
                    name={loc(item.name, lang)}
                    sizes="72px"
                    loading="lazy"
                    className="size-18 shrink-0 rounded-xl object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold">
                      <bdi>{loc(item.name, lang)}</bdi>
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {item.type === "variable" ? `${t("common.from")} ` : ""}
                      {formatPrice(menuItemDisplayPrice(item), lang)}
                    </p>
                  </div>
                  {directAdd ? (
                    <Button
                      size="sm"
                      disabled={Boolean(addingSuggestion) || quotePending}
                      onClick={() => void quickAdd(item)}
                    >
                      {t("cart.suggestionsAdd")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-expanded={configuring === item.id}
                      onClick={() =>
                        setConfiguring(configuring === item.id ? null : item.id)
                      }
                    >
                      {t("cart.suggestionsCustomize")}
                    </Button>
                  )}
                </div>
                {configuring === item.id ? (
                  <SuggestionConfigurator item={item} />
                ) : null}
              </article>
            );
          })}
        </div>
        <Button
          size="lg"
          className="w-full"
          disabled={quotePending}
          onClick={continueCheckout}
        >
          {t(suggestions.length ? "cart.suggestionsContinue" : "cart.checkout")}
        </Button>
      </DialogContent>
    </Dialog>
  );

  if (!hydrated)
    return (
      <div
        className="grid min-h-48 place-items-center"
        role="status"
        aria-label={t("common.loading")}
      >
        <Spinner />
      </div>
    );

  if (lineCount === 0) {
    return (
      <EmptyState
        title={t("cart.empty")}
        description={t("cart.emptyDesc")}
        icon={<ShoppingBag className="size-7" aria-hidden="true" />}
        action={
          <Button asChild variant="outline">
            <Link to="/menu">{t("cart.browse")}</Link>
          </Button>
        }
      />
    );
  }

  if (variant === "page") {
    return (
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px] xl:gap-7">
        <section className="grid gap-3">
          <header className="surface-card flex flex-wrap items-center justify-between gap-3 bg-muted/30 px-4 py-4 sm:px-5">
            <div>
              <h2 className="text-lg font-bold">{t("checkout.items")}</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {t(lineCount === 1 ? "cart.line" : "cart.lines", {
                  count: lineCount,
                })}
              </p>
            </div>
            {onClear ? (
              <Button
                variant="ghost"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onClear}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                {t("cart.clearAction")}
              </Button>
            ) : null}
          </header>
          <div className="grid gap-3">
            {cart.lines.map((line) => (
              <CartLineRow
                key={line.id}
                line={line}
                comfortable
                card
                onQuantity={(quantity) => setQuantity(line.id, quantity)}
                onRemove={() => removeLine(line.id)}
              />
            ))}
          </div>
        </section>

        <aside className="surface-card grid h-fit content-start gap-5 p-4 sm:p-5 lg:sticky lg:top-24">
          <h2 className="text-lg font-bold">{t("checkout.summary")}</h2>
          {linkedOffer}
          <LoyaltyReward />
          {promotionField}
          <CartTotals comfortable />
          {checkoutButton}
          {suggestionsDialog}
        </aside>
      </div>
    );
  }

  return (
    <div className="flex max-h-full flex-col gap-3">
      {showHeader ? (
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{t("cart.title")}</h2>
          <span className="text-xs font-bold text-muted-foreground">
            {t(lineCount === 1 ? "cart.line" : "cart.lines", {
              count: lineCount,
            })}
          </span>
        </div>
      ) : (
        <span className="text-xs font-bold text-muted-foreground">
          {t(lineCount === 1 ? "cart.line" : "cart.lines", {
            count: lineCount,
          })}
        </span>
      )}
      <div className="scrollbar-slim max-h-[45vh] overflow-y-auto pe-1">
        {cart.lines.map((line) => (
          <CartLineRow
            key={line.id}
            line={line}
            compact
            onQuantity={(q) => setQuantity(line.id, q)}
            onRemove={() => removeLine(line.id)}
          />
        ))}
      </div>
      {linkedOffer}
      <LoyaltyReward />
      {promotionField}
      <CartTotals />
      {checkoutButton}
      {suggestionsDialog}
    </div>
  );
}

function SuggestionConfigurator({ item }: { item: MenuItem }) {
  const [variationId, setVariationId] = useState(item.variations[0]?.id ?? "");
  const variation = item.variations.find(
    (candidate) => candidate.id === variationId,
  );
  return (
    <div className="mt-4 border-t border-border pt-4">
      <ProductDetailConfigurator
        item={item}
        variation={variation}
        onVariationChange={setVariationId}
      />
    </div>
  );
}
